import { createSelector } from '@reduxjs/toolkit';
import { selectors as dataGridTableSelectors } from '../Modules/dataGridTable';
import { selectors as filtersSelectors } from '../Modules/filters';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import { transform as transformReportList } from 'utils/DataTransform/Patrols/PatrolReportList';
import dayjs from 'dayjs';
import { transform as transformReportDetails } from 'utils/DataTransform/Patrols/patrolReportDetails.js';
import { getPatrolTemplateDetailTransformed } from '../../PatrolTemplates/Selectors/patrolTemplateSelectors';
import { ROLES } from 'utils/constants';

export const getPatrolTaskListTransformed = createSelector(
  dataGridTableSelectors.getGridTableList,
  list => {
    if (!list) return [];
    const currentTime = dayjs();
    return transformReportList(list, currentTime);
  },
);

export const getPatrolTaskListRaw = createSelector(
  dataGridTableSelectors.getGridTableList,
  list => {
    if (isEmpty(list)) return [];
    return list;
  },
);

export const getForceRefresh = createSelector(
  dataGridTableSelectors.getForceRefresh,
  forceRefresh => forceRefresh,
);

export const getPatrolFilters = createSelector(
  filtersSelectors.getFilterValues,
  filterValues => {
    return filterValues;
  },
);

export const getTotalRowsCount = createSelector(
  dataGridTableSelectors.getTotalGridRows,
  rowsCount => {
    if (!rowsCount) {
      return 0;
    }
    return rowsCount;
  },
);

export const getPatrolTaskDetailsRaw = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  details => {
    if (isEmpty(details)) {
      return {};
    }
    return details;
  },
);
export const getPatrolTaskId = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  details => {
    if (isEmpty(details)) {
      return {};
    }
    return details.id;
  },
);

export const getPatrolTaskDetailsTransformed = createSelector(
  getPatrolTaskDetailsRaw,
  details => {
    if (isEmpty(details)) {
      return {};
    }
    // transform
    const completedEvidences = details?.completedEvidencesMap
      ? details.completedEvidencesMap
          .map(m => m.completedEvidences)
          .flatMap(f => f)
      : [];
    const currentTime = dayjs();
    return transformReportDetails({
      data: details,
      currentTime,
      evidence: completedEvidences,
    });
  },
);

export const checkIfFirstPointPatrolled = createSelector(
  getPatrolTaskDetailsRaw,
  details => {
    if (isEmpty(details)) {
      return false;
    }
    return details.firstLocation?.timestamp; // if the patrol point is timestamped, then it must be patrolled
    // it may or may not have a taskId which signifies if an evidence was attached to the location or not
  },
);

export const getPatrolEvidenceCheckpointList = createSelector(
  getPatrolTemplateDetailTransformed,
  getPatrolTaskDetailsRaw,
  (patrolDefDetails, patrolTaskDetails) => {
    if (isEmpty(patrolTaskDetails)) {
      return patrolDefDetails?.detailedInfo?.checkPointDetail?.value;
    }
    const completedEvidences = patrolTaskDetails.completedEvidencesMap; // completed evidences

    const transformedCheckPointDetail = patrolDefDetails?.detailedInfo?.checkPointDetail?.value
      .map(cp => {
        const foundEvidence = find(completedEvidences, {
          templateId: cp.taskId,
        });
        if (foundEvidence) {
          return {
            ...cp,
            evidence: foundEvidence.completedEvidences,
            outsideTaskId: foundEvidence.outsideTaskId,
          };
        }
        return cp;
      })
      .map(cp => {
        // for first location, this means that the evidence is complete
        if (
          (patrolTaskDetails?.firstLocation?.timestamp ||
            patrolTaskDetails?.firstLocation?.taskSuccessful) &&
          cp?.outsideTaskId === patrolTaskDetails?.firstLocation?.taskId &&
          cp.text === 1
        ) {
          return {
            ...cp,
            patrolled: true,
          };
        }
        if (
          // if there is no evidence attached but the firstLocation is patrolled
          (patrolTaskDetails?.firstLocation?.timestamp ||
            patrolTaskDetails?.firstLocation?.taskSuccessful) &&
          cp.text === 1
        ) {
          return {
            ...cp,
            patrolled: true,
          };
        }
        // for other locations
        // find stamped locations
        const isOtherlocationsStamped = patrolTaskDetails.locations.filter(
          f => f?.timestamp || f?.taskSuccessful,
        );
        if (isOtherlocationsStamped.length > 0) {
          if (
            //match the taskID's if attached
            isOtherlocationsStamped.find(
              f => f.taskId === cp.outsideTaskId && f.timestamp,
            )
          ) {
            return {
              ...cp,
              patrolled: true,
            };
          }
          // or match the qr code
          const qrCodeMatch = isOtherlocationsStamped.find(
            f => f.definedLocation.qrCode === cp.qrCode,
          );
          if (qrCodeMatch) {
            return {
              ...cp,
              patrolled: true,
            };
          }
        }

        return cp;
      });

    // check firstLocation and locations keys in patrolTaskDetails
    // if any of the locations has taskSuccessful or timestamp is not null
    // if any of the condition is true, mark that point patrolled key as true
    return transformedCheckPointDetail;
  },
);

export const getPatrolTaskStatus = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  details => {
    if (isEmpty(details)) {
      return {};
    }
    return details.currentStatus;
  },
);
export const getPatrollerId = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  details => {
    if (isEmpty(details)) {
      return {};
    }
    return details.patroller.general.id;
  },
);
export const getIsPatrollerGuardOrResponder = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  details => {
    if (isEmpty(details)) {
      return {};
    }
    return (
      details.patroller.general.roles.includes(ROLES.GUARD.id) ||
      details.patroller.general.roles.includes(ROLES.RESPONDER.id)
    );
  },
);
export const getPatrolId = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  details => {
    if (isEmpty(details)) {
      return {};
    }
    return details.id;
  },
);
